<script setup lang="ts">
import { computed } from "vue";
import { type DecorScreen, nullDecor } from "#ui/decoration/screen";
import { KSO_PAY_DEFAULT, type KsoPayControl, ksoPayVariants } from "./ksoPayControls";

export interface Props {
	bonusesAvailable: boolean;
	bonusesOpen: boolean;
	bonusesText: string;
	payLabel: string;
	payAmount: string;
	payDisabled: boolean;
	decor?: DecorScreen;
}

const props = withDefaults(defineProps<Props>(), { decor: () => nullDecor });

const emit = defineEmits<{ "toggle-bonuses": []; pay: [] }>();

const payContent = computed(() => ksoPayVariants[props.decor.variant("kso_pay_button", KSO_PAY_DEFAULT)] ?? ksoPayVariants[KSO_PAY_DEFAULT]);
const payControl = computed(
	(): KsoPayControl => ({
		text: `${props.payLabel} ${props.payAmount}`,
		label: props.payLabel,
		amount: props.payAmount,
		labelAttrs: props.decor.bind("kso_pay_label"),
		amountAttrs: props.decor.bind("kso_pay_amount"),
	}),
);
</script>

<template>
	<div class="kso_created_actions tw:flex tw:items-stretch tw:gap-12" v-bind="props.decor.bind('kso_actions')">
		<button
			v-if="props.bonusesAvailable"
			type="button"
			class="kso_created_btn kso_created_btn_ghost tw:flex-[0_1_42%] tw:min-h-56 tw:p-[12px_20px] tw:rounded-[40px] tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:[outline:none] tw:[-webkit-tap-highlight-color:transparent] tw:select-none tw:[word-break:break-word] tw:[transition:transform_0.12s_ease,background-color_0.2s_ease,opacity_0.2s_ease] tw:active:[transform:scale(0.98)] tw:disabled:opacity-[0.45] tw:[border:1px_solid_rgba(255,255,255,0.28)] tw:[background:rgba(255,255,255,0.08)] tw:text-[#fff] tw:data-[active=true]:[background:rgba(255,255,255,0.2)] tw:min-[1023px]:min-h-76 tw:min-[1023px]:[font-size:calc(1.5rem*var(--fontFit-x,1))]"
			:data-active="props.bonusesOpen"
			v-bind="props.decor.bind('kso_bonus_button', { active: props.bonusesOpen })"
			@click="emit('toggle-bonuses')"
		>
			{{ props.bonusesText }}
		</button>
		<button
			type="button"
			class="kso_created_btn kso_created_btn_primary tw:flex-1 tw:min-h-56 tw:p-[12px_20px] tw:rounded-[40px] tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:[font-size:calc(1rem*var(--fontFit-x,1))] tw:[outline:none] tw:[-webkit-tap-highlight-color:transparent] tw:select-none tw:[word-break:break-word] tw:[transition:transform_0.12s_ease,background-color_0.2s_ease,opacity_0.2s_ease] tw:active:[transform:scale(0.98)] tw:disabled:opacity-[0.45] tw:[border:none] tw:[background:#fff] tw:text-(--accentColor) tw:min-[1023px]:min-h-76 tw:min-[1023px]:[font-size:calc(1.5rem*var(--fontFit-x,1))]"
			:disabled="props.payDisabled"
			v-bind="props.decor.bind('kso_pay_button', { disabled: props.payDisabled })"
			@click="emit('pay')"
		>
			<component :is="payContent" :control="payControl" />
		</button>
	</div>
</template>
